import * as React from "react";
import cn from "classnames";
import { generatePath, useMatch, useParams } from "react-router-dom";
import * as Team from "src/state/modules/team";
import { TeamInfoSection } from "./TeamInfoSection";
import Styles from "./TeamNavBar.module.scss";
import Sticky from "../../atoms/Sticky/StickyItem.module.scss";
import { TabNavBar } from "src/components/atoms/TabNavBar/TabNavBar";
import { ROUTES } from "src/utils/constants/routes";

export interface RouteComponentProps {
  team: Team.Team;
}

export const TeamNavBar = ({ team }: RouteComponentProps) => {
  const { id, pid } = useParams<string>();
  const teamID = id !== undefined ? id : null;

  const homePath = generatePath(ROUTES.teams.home, { id: teamID });
  const schedulePath = generatePath(ROUTES.teams.events.schedule, { id: teamID });
  const statsPath = generatePath(ROUTES.teams.stats, { id: teamID });
  const teamInfoPath = generatePath(ROUTES.teams.teamInfo, { id: teamID });
  const pitchSmartPath = generatePath(ROUTES.teams.pitchSmart, { id: teamID });
  const usersPath = generatePath(ROUTES.teams.users, { id: teamID });
  const [shrinkHeader, setShrinkHeader] = React.useState(false);
  const navRef = React.useRef<HTMLDivElement>(null);

  // Publish the pinned TeamNavBar's height so page-level sticky offsets (e.g. the
  // schedule's month dividers) can sit just below it regardless of its height.
  React.useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const publishHeight = () => document.documentElement.style.setProperty("--team-nav-height", `${el.offsetHeight}px`);
    publishHeight();
    const observer = new ResizeObserver(publishHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      // Don't leave a stale height behind for pages without a TeamNavBar
      // (e.g. event pages) — their sticky offsets must not include it.
      document.documentElement.style.removeProperty("--team-nav-height");
    };
  }, []);

  const teamPlayerPath = generatePath(ROUTES.teams.playerProfile, { id: teamID, pid: pid ?? "" });
  const teamInfoMatch = useMatch({ path: teamInfoPath, end: true });
  const teamPlayerMatch = useMatch({ path: teamPlayerPath, end: true });

  const teamNavOptions = [
    {
      label: "Home",
      path: homePath,
      active: useMatch({ path: homePath, end: true }) !== null,
      "data-testid": "team-nav-bar-home",
      isShowing: true,
      isEvent: false,
    },
    {
      label: "Schedule",
      path: schedulePath,
      active: useMatch({ path: schedulePath, end: true }) !== null,
      "data-testid": "team-nav-bar-schedule",
      isShowing: true,
      isEvent: false,
    },
    {
      label: "Team",
      path: teamInfoPath,
      active: teamInfoMatch !== null || teamPlayerMatch !== null,
      "data-testid": "team-nav-bar-team",
      isShowing: true,
      isEvent: false,
    },
    {
      label: "Stats",
      path: statsPath,
      active: useMatch({ path: statsPath, end: true }) !== null,
      "data-testid": "team-nav-bar-stats",
      isShowing: true,
      isEvent: false,
    },
    {
      label: "PitchSmart",
      path: pitchSmartPath,
      active: useMatch({ path: pitchSmartPath, end: true }) !== null,
      "data-testid": "team-nav-bar-pitch-smart",
      isShowing: true,
      isEvent: false,
    },
    {
      label: "Users",
      path: usersPath,
      active: useMatch({ path: usersPath, end: true }) !== null,
      "data-testid": "team-nav-bar-users",
      isShowing: true,
      isEvent: false,
    },
  ];

  return (
    <div ref={navRef} className={cn(Sticky.stickyItem, Styles.stickyItem)}>
      <div className={cn(Styles.navBar, { [Styles.shrunk]: shrinkHeader })}>
        <TeamInfoSection shrinkHeader={shrinkHeader} setShrinkHeader={setShrinkHeader} team={team} />
      </div>
      <div className={cn(Styles.tabsSticky)}>
        <TabNavBar className={cn(Styles.tabs)} options={teamNavOptions} />
      </div>
    </div>
  );
};
